import type { MarketingPaymentListRow, MarketingPaymentStatus } from '../../types/marketingNotas'
import { documentTypeBadgeClass, documentTypeLabel } from './notasUi'

export const MODULE_NAME = 'marketing_financeiro'
export const PERM_LOADING = null
export const PAGE_SIZE = 20

export const ICON_BTN_CLASS =
  'rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700'

export function paymentStatusLabel(status: MarketingPaymentStatus | string): string {
  return status === 'paid' ? 'Pago' : 'Pendente'
}

export function paymentStatusBadgeClass(status: MarketingPaymentStatus | string): string {
  return status === 'paid'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
}

export type FinanceiroViewMode = 'lista' | 'agrupado'

export type PaymentDocumentGroup = {
  key: string
  kind: 'invoice' | 'contract' | 'expense'
  title: string
  supplierName: string
  status: string
  rows: MarketingPaymentListRow[]
  total: number
  pending: number
  paidCount: number
  nextDue: string | null
  overdue: boolean
}

export function paymentSupplierName(row: MarketingPaymentListRow): string {
  return (
    row.invoice?.supplier?.name ||
    row.contract?.supplier?.name ||
    row.expense?.supplier?.name ||
    ''
  )
}

export function paymentDocumentStatus(row: MarketingPaymentListRow): string {
  return row.invoice?.status || row.contract?.status || row.expense?.status || ''
}

export function paymentCompetenceLabel(row: MarketingPaymentListRow): string {
  const competence = row.recurrence_competence?.slice(0, 7)
  if (!competence) return ''
  const [year, month] = competence.split('-')
  return `${month}/${year}`
}

export function paymentDocumentTitle(row: MarketingPaymentListRow): string {
  if (row.invoice) {
    return row.invoice.series ? `${row.invoice.number} / ${row.invoice.series}` : row.invoice.number
  }
  if (row.expense) {
    return row.expense.action_name || '—'
  }
  const contractName = row.contract?.name || '—'
  const competenceLabel = paymentCompetenceLabel(row)
  return competenceLabel ? `${contractName} · ${competenceLabel}` : contractName
}

export function paymentDocumentName(row: MarketingPaymentListRow): string {
  if (row.expense?.action_name) return row.expense.action_name
  if (row.contract?.name) return row.contract.name
  return '—'
}

export function paymentDocumentNumber(row: MarketingPaymentListRow): string {
  if (row.invoice) {
    return row.invoice.series ? `${row.invoice.number} / ${row.invoice.series}` : row.invoice.number
  }
  if (row.expense?.action_name) return row.expense.action_name
  return '—'
}

export function paymentDocumentKindLabel(row: MarketingPaymentListRow): string {
  if (row.invoice?.document_type) return documentTypeLabel(row.invoice.document_type)
  if (row.contract) return 'Contrato'
  if (row.expense) return 'Despesa'
  return '—'
}

export function paymentDocumentKindBadgeClass(row: MarketingPaymentListRow): string {
  if (row.invoice?.document_type) return documentTypeBadgeClass(row.invoice.document_type)
  if (row.contract) {
    return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200'
  }
  if (row.expense) {
    return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
}

export function paymentDocumentRefLabel(row: MarketingPaymentListRow): string {
  if (row.invoice) return `nota ${row.invoice.number}`
  if (row.contract) return `contrato ${row.contract.name || ''}`
  if (row.expense) return `despesa ${row.expense.action_name || ''}`
  return 'documento'
}

export function buildPaymentGroups(rows: MarketingPaymentListRow[]): PaymentDocumentGroup[] {
  const map = new Map<string, MarketingPaymentListRow[]>()
  for (const row of rows) {
    const key = row.invoice_id
      ? `invoice:${row.invoice_id}`
      : row.contract_id
        ? `contract:${row.contract_id}:${row.recurrence_competence || 'base'}`
        : row.expense_id
          ? `expense:${row.expense_id}`
          : `payment:${row.id}`
    const bucket = map.get(key)
    if (bucket) bucket.push(row)
    else map.set(key, [row])
  }

  const groups: PaymentDocumentGroup[] = []
  for (const [key, groupRows] of map) {
    const sorted = [...groupRows].sort((a, b) => {
      const byDue = a.due_date.localeCompare(b.due_date)
      if (byDue !== 0) return byDue
      return a.installment_number - b.installment_number
    })
    const sample = sorted[0]
    let pending = 0
    let total = 0
    let paidCount = 0
    let overdue = false
    let nextDue: string | null = null
    for (const row of sorted) {
      if (row.amount != null) {
        const amount = Number(row.amount)
        total += amount
        if (row.payment_status !== 'paid') pending += amount
      }
      if (row.payment_status === 'paid') paidCount += 1
      else if (!nextDue || row.due_date < nextDue) nextDue = row.due_date
      if (isPaymentOverdue(row.payment_status, row.due_date)) overdue = true
    }
    if (!nextDue) nextDue = sorted[0]?.due_date || null
    groups.push({
      key,
      kind: sample.invoice ? 'invoice' : sample.expense ? 'expense' : 'contract',
      title: paymentDocumentTitle(sample),
      supplierName: paymentSupplierName(sample) || '—',
      status: paymentDocumentStatus(sample),
      rows: sorted,
      total: Math.round(total * 100) / 100,
      pending: Math.round(pending * 100) / 100,
      paidCount,
      nextDue,
      overdue,
    })
  }

  groups.sort((a, b) => {
    const due = (a.nextDue || '9999').localeCompare(b.nextDue || '9999')
    if (due !== 0) return due
    return a.title.localeCompare(b.title, 'pt-BR')
  })
  return groups
}

export function isPaymentOverdue(
  status: MarketingPaymentStatus | string,
  dueDate: string | null | undefined,
  today = new Date().toISOString().slice(0, 10)
): boolean {
  if (status === 'paid' || !dueDate) return false
  return dueDate.slice(0, 10) < today
}
